
import NavBar from "@/components/woost/NavBar";
import Footer from "@/components/woost/Footer";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const DataProtection = () => {
  return (
    <div className="min-h-screen bg-white">
      <NavBar />
      
      <motion.div 
        className="container mx-auto px-4 py-20"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Link to="/" className="inline-flex items-center text-woost-blue mb-8 hover:underline">
          <ArrowLeft size={16} className="mr-2" />
          Back to Home
        </Link>
        
        <h1 className="text-4xl font-bold mb-8">Data Protection Policy</h1>
        
        <div className="prose prose-lg max-w-none">
          <p>Last Updated: April 18, 2025</p>
          
          <h2>1. Introduction</h2>
          <p>At Woost Internet Private Limited ("Woost," "we," "us," or "our"), we are committed to protecting the personal data of our users. This Data Protection Policy outlines our practices concerning the collection, use, storage, and protection of personal data in compliance with applicable data protection laws.</p>
          
          <h2>2. Data Protection Principles</h2>
          <p>We adhere to the following principles when processing personal data:</p>
          <ul>
            <li><strong>Lawfulness, fairness, and transparency:</strong> We process personal data lawfully, fairly, and in a transparent manner.</li>
            <li><strong>Purpose limitation:</strong> We collect personal data for specified, explicit, and legitimate purposes and do not process it in a manner incompatible with those purposes.</li>
            <li><strong>Data minimization:</strong> We limit the collection of personal data to what is necessary for the purposes for which it is processed.</li>
            <li><strong>Accuracy:</strong> We take reasonable steps to ensure that personal data is accurate and, where necessary, kept up to date.</li>
            <li><strong>Storage limitation:</strong> We retain personal data only for as long as necessary for the purposes for which it is processed.</li>
            <li><strong>Integrity and confidentiality:</strong> We process personal data in a manner that ensures appropriate security, including protection against unauthorized or unlawful processing and against accidental loss, destruction, or damage.</li>
            <li><strong>Accountability:</strong> We are responsible for demonstrating compliance with these principles.</li>
          </ul>
          
          <h2>3. Legal Basis for Processing</h2>
          <p>We process personal data only if we have a valid legal basis, which may include:</p>
          <ul>
            <li>The individual's consent</li>
            <li>Performance of a contract with the individual</li>
            <li>Compliance with a legal obligation</li>
            <li>Protection of vital interests of the individual or another person</li>
            <li>Performance of a task carried out in the public interest</li>
            <li>Legitimate interests pursued by us or a third party</li>
          </ul>
          
          <h2>4. Data Security Measures</h2>
          <p>We implement appropriate technical and organizational measures to ensure a level of security appropriate to the risk, including:</p>
          <ul>
            <li>Encryption of personal data</li>
            <li>Ability to ensure ongoing confidentiality, integrity, availability, and resilience of processing systems and services</li>
            <li>Ability to restore availability and access to personal data in a timely manner in the event of a physical or technical incident</li>
            <li>Regular testing, assessing, and evaluating the effectiveness of security measures</li>
            <li>Access controls and authentication mechanisms</li>
            <li>Regular security awareness training for employees</li>
            <li>Physical security measures for our premises and data centers</li>
          </ul>
          
          <h2>5. Data Breach Procedures</h2>
          <p>In the event of a personal data breach, we will:</p>
          <ul>
            <li>Investigate the breach to determine its scope and impact</li>
            <li>Take measures to contain and mitigate the breach</li>
            <li>Notify the relevant supervisory authority without undue delay, where required</li>
            <li>Notify affected individuals where there is a high risk to their rights and freedoms</li>
            <li>Document the breach, including facts, effects, and remedial action taken</li>
          </ul>
          
          <h2>6. Data Protection Impact Assessments</h2>
          <p>We conduct data protection impact assessments for processing activities that are likely to result in a high risk to individuals' rights and freedoms, particularly when implementing new technologies or undertaking large-scale processing of sensitive data.</p>
          
          <h2>7. International Data Transfers</h2>
          <p>When transferring personal data outside the country of collection, we ensure appropriate safeguards are in place to protect the data, such as:</p>
          <ul>
            <li>Standard contractual clauses</li>
            <li>Binding corporate rules</li>
            <li>Adequacy decisions by relevant authorities</li>
            <li>Explicit consent from the individual</li>
          </ul>
          
          <h2>8. Data Subject Rights</h2>
          <p>We respect and facilitate the exercise of individuals' rights under data protection laws, including:</p>
          <ul>
            <li>Right to access their personal data</li>
            <li>Right to rectification of inaccurate data</li>
            <li>Right to erasure ("right to be forgotten")</li>
            <li>Right to restriction of processing</li>
            <li>Right to data portability</li>
            <li>Right to object to processing</li>
            <li>Rights related to automated decision-making and profiling</li>
          </ul>
          
          <h2>9. Data Protection Training</h2>
          <p>We provide regular data protection training to our employees who have access to personal data to ensure they understand their responsibilities and comply with this policy and applicable data protection laws.</p>
          
          <h2>10. Data Protection Officer</h2>
          <p>We have appointed a Data Protection Officer who is responsible for overseeing our data protection strategy and implementation. The Data Protection Officer can be contacted at:</p>
          <p>
            Data Protection Officer<br />
            Woost Internet Private Limited<br />
            H-102, Third floor, Sector 63 Rd, H Block, Sector 63, Noida, Uttar Pradesh 201301<br />
            Email: dpo@woost.io
          </p>
          
          <h2>11. Changes to This Policy</h2>
          <p>We may update this Data Protection Policy from time to time. We will notify you of any changes by posting the new policy on our website and updating the "Last Updated" date. You are advised to review this policy periodically for any changes.</p>
          
          <h2>12. Contact Us</h2>
          <p>If you have any questions about this Data Protection Policy, please contact us at:</p>
          <p>
            Woost Internet Private Limited<br />
            H-102, Third floor, Sector 63 Rd, H Block, Sector 63, Noida, Uttar Pradesh 201301<br />
            Email: partners@woost.io
          </p>
        </div>
      </motion.div>
      
      <Footer />
    </div>
  );
};

export default DataProtection;
